import { useFormik } from 'formik';
import { initialValues, validationSchema } from './formData';
import { ChatAssistantProps } from './props';
import { useEffect, useState } from 'react';
import { asyncGetProjects } from '../../../../../services/projects.service';
import { Attachment, ISelect } from '../../../../../shared/types/shared';
import { formatParams } from '../../../../../shared/utils/params';
import { fileToBase64 } from '../../../../../shared/utils/files';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { AttachmentActions } from './actions';
import { TableTooltip } from '../../../../components/general/table';
import { DateTooltip } from '../../../../components/general/dateTooltip';
import {
    asyncCreateBasisChatAssistant,
    asyncDeleteMultipleAttachmentChatAssistant,
} from '../../../../../services/chat-assistant.service';
import { useNavigate } from 'react-router-dom';
import { CHAT_ASSISTANT_EDIT } from '../../../../../routes/paths';
import Swal from 'sweetalert2';
import { useAuthContext } from '../../../../../contexts/authContext';
import variables from '../../../../../shared/styles/settings.module.scss';
import { ToastSwal } from '../../../../components/general/toastSwals/toastSwal';
import { asyncGetUsersByProject } from '../../../../../services/users.service';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../redux/store';
import { isAdminUser } from '../../../../../shared/utils/user';

export const useChatAssistantData = ({
    data,
    setLoading,
    setActiveStep,
    handleCompleteStep,
    setOpenApiKeyAlert,
    setOpenNotFoundApiKey,
    sharepointItems,
    setSharepointItems,
}: ChatAssistantProps) => {
    const [projects, setProjets] = useState<ISelect[]>([]);
    const [users, setUsers] = useState<ISelect[]>([]);
    const [deleted, setDeleted] = useState<string[]>([]);
    const [isSearched, setIsSearched] = useState(false);
    const [isApiKeyFounded, setIsApiKeyFounded] = useState(false);
    const [isAssistantOwnerOrAdmin, setIsAssistantOwnerOrAdmin] =
        useState(false);

    const navigate = useNavigate();
    const { logUser } = useSelector((state: RootState) => state.general);
    const { id: userId, type } = logUser || {};

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });
    const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>(
        []
    );
    const { modeTheme } = useAuthContext();

    const formik = useFormik({
        initialValues,
        validationSchema: validationSchema(isApiKeyFounded, isSearched),
        onSubmit: async () => await createBasicAssistant(),
    });

    const iconColor =
        modeTheme === 'darkMode' ? variables.darkColor : variables.primaryColor;

    const handleDelete = (id: string | number) => {
        if (!Boolean(id?.toString()?.trim())) return;
        setDeleted((prev) => [...prev, id.toString().trim()]);
    };

    const handleDeleteMultiple = () => {
        if (selectionModel.length === 0) return;
        Swal.fire({
            title: 'Eliminar',
            text: '¿Está seguro que desea eliminar los archivos seleccionados?',
            icon: 'question',
            iconColor,
            showDenyButton: true,
            denyButtonText: 'NO',
            denyButtonColor: iconColor,
            confirmButtonText: 'SI',
            confirmButtonColor: iconColor,
            customClass: 'swal-wide-class',
        }).then((response) => {
            if (response.isConfirmed) {
                asyncDeleteMultipleAttachmentChatAssistant(selectionModel).then(
                    (body: any) => {
                        const isSuccessful = body.items !== undefined;
                        Swal.fire({
                            text: isSuccessful
                                ? 'Archivos eliminados con éxito'
                                : 'Error al eliminar los archivos',
                            icon: isSuccessful ? 'success' : 'error',
                            iconColor,
                        });
                        if (isSuccessful) {
                            setDeleted((prev) => [
                                ...prev,
                                ...selectionModel.map((id) => id.toString()),
                            ]);
                            setSelectionModel([]);
                        }
                    }
                );
            }
        });
    };

    const columns: GridColDef<Attachment>[] = [
        {
            field: 'actions',
            headerName: 'Acciones',
            width: 100,
            sortable: false,
            disableColumnMenu: true,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row) => (
                <AttachmentActions
                    attachment={row.row}
                    onDelete={handleDelete}
                />
            ),
        },
        {
            field: 'name',
            headerName: 'Nombre',
            minWidth: 150,
            flex: 0.6,
            renderCell: (row) => <TableTooltip value={row.row.name || ''} />,
        },
        {
            field: 'createdBy',
            headerName: 'Creado por',
            minWidth: 150,
            flex: 0.6,
            renderCell: (row) => (
                <TableTooltip
                    value={
                        row.row.createdBy
                            ? `(${row.row.createdBy.username}) ${row.row.createdBy.name} ${row.row.createdBy.surname}`
                            : ''
                    }
                />
            ),
        },
        {
            field: 'created_at',
            headerName: 'Fecha de creación',
            minWidth: 120,
            flex: 0.4,
            renderCell: (row) => (
                <DateTooltip date={row.row.created_at} showFornow />
            ),
        },
    ];

    const createBasicAssistant = async () => {
        handleCompleteStep(0);
        setSelectionModel([]);

        if (data) {
            setActiveStep(1);
            return;
        }

        setLoading(true);

        const {
            name,
            description,
            project,
            attachments = [],
            isAdvancedMode,
            apiKeyIa,
            displayName,
            authorizedManagers,
        } = formik.values;

        const authorizedManagersIds = authorizedManagers?.map(
            (item) => item?.id
        );

        const sharepointData: any = [];

        sharepointItems.forEach((f) =>
            sharepointData.push({
                id: f.id,
                name: f.name,
                isSharepoint: true,
                isFolder: !!f.folder,
                driveId: f?.parentReference?.driveId || f?.driveId, // MyDrive or SharePoint
            })
        );

        const allAttachments = [
            ...(attachments as Attachment[]),
            ...sharepointData,
        ];

        const createdBasicAssistant = await asyncCreateBasisChatAssistant({
            name,
            description,
            project,
            attachments: allAttachments || [],
            isAdvancedMode,
            displayName,
            authorizedManagers: authorizedManagersIds,
        });

        if (createdBasicAssistant?.errors) {
            setIsSearched(false);
            if (
                createdBasicAssistant.errors[
                    '[CHAT_ASSISTANT] INVALID_FILE_TYPE'
                ]
            ) {
                ToastSwal.fire({
                    text: 'Solo puedes subir archivos de SharePoint con estas extensiones: .docx, .pptx, .xlsx, .pdf o .txt',
                    icon: 'error',
                    timer: 5000,
                    position: 'center-end',
                });
            } else if (
                createdBasicAssistant.errors['[CHAT_ASSISTANT] DUPLICATED']
            ) {
                ToastSwal.fire({
                    text: 'Ya existe un asistente con este nombre. Usa otro nombre e inténtalo de nuevo.',
                    icon: 'warning',
                    timer: 5000,
                    position: 'center-end',
                });
            } else {
                ToastSwal.fire({
                    text: 'No se pudo crear el asistente. Intenta de nuevo más tarde.',
                    icon: 'error',
                    timer: 5000,
                    position: 'center-end',
                });
            }
        } else {
            setIsSearched(true);
            if (
                !createdBasicAssistant?.errors &&
                createdBasicAssistant?.items?.id
            ) {
                setOpenApiKeyAlert(true);
                setIsApiKeyFounded(true);
                setActiveStep(1);
                navigate(
                    `${CHAT_ASSISTANT_EDIT}/${createdBasicAssistant?.items?.id}`
                );
                setSharepointItems([]);
                formik.setFieldValue('attachments', []);
            } else {
                if (!apiKeyIa) {
                    setOpenNotFoundApiKey(true);
                } else {
                    setActiveStep(1);
                }
            }
        }

        setLoading(false);
    };

    const handleOnChangeFiles = async (files: File[]) => {
        const data: Attachment[] = [];
        for await (const file of files) {
            const res = await fileToBase64(file);
            if (res) data.push(res);
        }
        formik.setFieldValue('attachments', data);
    };

    const handlePaginationChange = (newPagination: {
        page: number;
        pageSize: number;
    }) => {
        setPaginationModel(newPagination);
    };

    const rows =
        data?.attachments?.filter(
            (item) => item.id && !deleted.includes(item.id?.toString())
        ) || [];

    const paginatedRows = rows.slice(
        paginationModel.page * paginationModel.pageSize,
        (paginationModel.page + 1) * paginationModel.pageSize
    );

    useEffect(() => {
        const authorizedManagers = data?.authorizedManagers?.map((item) => ({
            id: item?.id,
            name: `${item?.name} ${item?.surname} (${item?.username})`,
        }));
        formik?.setFieldValue('name', data?.name || '');
        formik?.setFieldValue('description', data?.description || '');
        formik?.setFieldValue('project', (data?.project as ISelect)?.id || '');
        formik?.setFieldValue('isAdvancedMode', data?.isAdvancedMode);
        formik?.setFieldValue('apiKeyIa', data?.apiKeyIa);
        formik?.setFieldValue('displayName', data?.displayName);
        formik?.setFieldValue('apiIntegration', data?.apiIntegration);
        formik?.setFieldValue('authorizedManagers', authorizedManagers);
    }, [data]);

    useEffect(() => {
        asyncGetProjects().then((projects) =>
            setProjets(formatParams(projects.items as ISelect[]))
        );
    }, []);

    useEffect(() => {
        asyncGetUsersByProject(formik?.values?.project as string).then(
            (users) =>
                setUsers(
                    formatParams(
                        (users?.items as ISelect[])?.map((item) => ({
                            ...item,
                            name: `${item?.name} ${item?.surname} (${item?.username})`,
                        }))
                    )
                )
        );
    }, [formik?.values?.project, data]);

    useEffect(() => {
        setIsAssistantOwnerOrAdmin(
            data?.createdBy?.id === userId || isAdminUser(type)
        );
    }, [data, userId, type]);

    return {
        formikChatAssistantData: formik,
        projects,
        handleOnChangeFiles,
        columns,
        rows,
        isSearched,
        isApiKeyFounded,
        paginatedRows,
        paginationModel,
        handlePaginationChange,
        setOpenNotFoundApiKey,
        selectionModel,
        setSelectionModel,
        handleDeleteMultiple,
        users,
        isAssistantOwnerOrAdmin,
    };
};
